/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { SkillAttainmentProgress_AttainmentStatus as AttainmentStatus } from '__generated__/graphql-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import { Button, Dialog, Grid, LoadingSection, Typography2 } from '@coursera/cds-core';

import type { ComputedItemType } from 'bundles/assess-common/lib/ComputedItem';
import type { QueryStateSuccessFragment } from 'bundles/assignment-submission/components/api/assignment-submission-api/__generated__/AssignmentSubmission';
import type { TrackingData } from 'bundles/assignment-submission/types/TrackingData';
import type { EnterpriseProgramFromSlugFragment } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import SkillProgressDetailedList from 'bundles/skills-common/shared/verified-skills/SkillProgressDetailedList';
import SkillsBadge from 'bundles/skills-common/shared/verified-skills/SkillsBadge';
import SkillsDialogAchievementEarned from 'bundles/skills-common/shared/verified-skills/SkillsDialogAchievementEarned';
import VerifiedSkillsBadgeDataProvider from 'bundles/skills-common/shared/verified-skills/context/pathway-badge/VerifiedSkillsBadgeDataProvider';
import { useSkillPathwayAttainmentsDataContext } from 'bundles/skills-common/shared/verified-skills/context/skill-pathway/SkillPathwayAttainmentsDataContext';
import SkillPathwayAttainmentsDataProvider from 'bundles/skills-common/shared/verified-skills/context/skill-pathway/SkillPathwayAttainmentsDataProvider';
import { useSkillPathwayEnrollmentDataContext } from 'bundles/skills-common/shared/verified-skills/context/skill-pathway/SkillPathwayEnrollmentDataContext';
import SkillPathwayEnrollmentDataProvider from 'bundles/skills-common/shared/verified-skills/context/skill-pathway/SkillPathwayEnrollmentDataProvider';
import { convertEnterpriseProgramFragment } from 'bundles/skills-common/shared/verified-skills/utils/convertEnterpriseProgramFragment';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import EnterpriseProgramDataProvider from 'bundles/unified-common/providers/EnterpriseProgramDataProvider';

import _t from 'i18n!nls/skills-common';

type Props = {
  open: boolean;
  onClose: () => void;
  focusedSkillAreaId?: string;

  // props to handle retrying assessment when entering from and assignment results page
  item?: ComputedItemType;
  itemMetadata?: ItemMetadata;
  submissionState?: QueryStateSuccessFragment;
  refetchAssignmentSubmissionData?: () => Promise<void>;
  trackingData?: TrackingData;

  // render prop to avoid circular dependency with assignment-submission
  renderRetryActionButton?: (
    submissionState: QueryStateSuccessFragment,
    itemMetadata: ItemMetadata,
    refetchAssignmentSubmissionData: () => Promise<void>,
    trackingData?: TrackingData
  ) => React.ReactNode;

  // enterprise context program
  enterpriseContextProgram?: EnterpriseProgramFromSlugFragment;
};

type WrapperProps = Props & {
  courseId: string;
};

const styles = {
  bodyText: css`
    margin-top: var(--cds-spacing-100);
  `,
  detailedListContainer: css`
    margin-top: var(--cds-spacing-300);
  `,
  loadingSection: css`
    padding: 200px 0;
  `,
};

const SkillsDialog = ({
  open,
  onClose,
  focusedSkillAreaId,
  item,
  itemMetadata,
  submissionState,
  refetchAssignmentSubmissionData,
  trackingData,
  renderRetryActionButton,
  enterpriseContextProgram,
}: Props) => {
  const { HeadingGroup, Content, Actions } = Dialog;
  const handleClose = () => onClose();
  const { skillAttainments, loading } = useSkillPathwayAttainmentsDataContext();
  const {
    program,
    enrolledBadgeCollection,
    skillPath,
    skillPathCurriculumProducts,
    loading: loadingEnrollmentData,
  } = useSkillPathwayEnrollmentDataContext();

  const skillName = skillPath?.title || '';
  const allSkillsComplete =
    enrolledBadgeCollection?.userState === 'ISSUED' &&
    skillAttainments?.every((skillAttainment) =>
      skillAttainment.skillExpressionsAttainment?.every(
        (skillExpressionAttainment) => skillExpressionAttainment.attainmentStatus === AttainmentStatus.FullAttainment
      )
    );
  const noSkillsAttainedForAllSkillAreas =
    !allSkillsComplete &&
    skillAttainments?.every((skillAttainment) =>
      skillAttainment.skillExpressionsAttainment?.every(
        (skillExpressionAttainment) => skillExpressionAttainment.attainmentStatus === AttainmentStatus.NoAttainment
      )
    );

  return (
    <Dialog
      width="large"
      aria-describedby="dialog-content"
      aria-labelledby="skills-dialog-title"
      open={open}
      variant="standard"
      onClose={handleClose}
      data-testid="skills-dialog"
    >
      <HeadingGroup id="skills-dialog-title">{_t('Your progress')}</HeadingGroup>

      <Content id="dialog-content">
        {loading || loadingEnrollmentData ? (
          <LoadingSection css={styles.loadingSection} />
        ) : (
          <React.Fragment>
            {allSkillsComplete && skillAttainments?.length ? (
              <SkillsDialogAchievementEarned skillName={skillName} numberOfSkills={skillAttainments.length} />
            ) : (
              <Grid container spacing={4}>
                <Grid item xs={10}>
                  <Typography2 variant="subtitleLarge" component="h3">
                    {noSkillsAttainedForAllSkillAreas
                      ? _t('Complete the assessments to verify your skills')
                      : _t("You're on the right track! Keep up the momentum!")}
                  </Typography2>
                  <Grid container css={styles.bodyText}>
                    <Grid item xs={12}>
                      <Typography2 variant="bodySecondary" component="p" color="supportText">
                        <FormattedMessage
                          message={_t('Master all skills to earn the {skillName} certificate.')}
                          skillName={skillName}
                        />
                      </Typography2>
                      <Typography2 variant="bodySecondary" component="p" color="supportText">
                        <FormattedMessage
                          message={_t(
                            'Already proficient in some of these skills? Just take the related assessment to verify your skills.'
                          )}
                        />
                      </Typography2>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container item xs={2} justifyContent="flex-end">
                  <SkillsBadge />
                </Grid>
              </Grid>
            )}
            <Grid container css={styles.detailedListContainer}>
              <Grid item xs={12}>
                <SkillProgressDetailedList
                  skillAreaAttainments={skillAttainments || []}
                  focusedSkillAreaId={focusedSkillAreaId}
                  item={item}
                  submissionState={submissionState}
                  itemMetadata={itemMetadata}
                  refetchAssignmentSubmissionData={refetchAssignmentSubmissionData}
                  trackingData={trackingData}
                  renderRetryActionButton={renderRetryActionButton}
                  enrollmentProps={{
                    programId: program?.id || enterpriseContextProgram?.id || '',
                    pathwayCollectionId: enrolledBadgeCollection?.collectionId,
                    programSlug: program?.metadata?.slug || enterpriseContextProgram?.slug,
                    thirdPartyOrganizationSlug: program?.thirdPartyOrg?.slug || '',
                  }}
                  skillPathCurriculumProducts={skillPathCurriculumProducts}
                  program={
                    program ||
                    (enterpriseContextProgram ? convertEnterpriseProgramFragment(enterpriseContextProgram) : undefined)
                  }
                  enrolledBadgeCollection={enrolledBadgeCollection}
                  skillPath={skillPath}
                  handleDialogOnClose={handleClose}
                />
              </Grid>
            </Grid>
          </React.Fragment>
        )}
      </Content>

      <Actions>
        <Button size="medium" variant="primary" onClick={() => onClose()}>
          {_t('Close')}
        </Button>
      </Actions>
    </Dialog>
  );
};

const SkillsDialogWithAttainmentData = ({
  open,
  onClose,
  courseId,
  focusedSkillAreaId,
  item,
  itemMetadata,
  submissionState,
  refetchAssignmentSubmissionData,
  trackingData,
  renderRetryActionButton,
}: WrapperProps) => {
  const enterpriseContextProgram = useProgram();
  return (
    <SkillPathwayEnrollmentDataProvider
      courseId={courseId}
      overrideProgramId={enterpriseContextProgram?.id}
      overrideCurriculumId={enterpriseContextProgram?.naptimeCurriculum?.id ?? undefined}
    >
      <VerifiedSkillsBadgeDataProvider>
        <SkillPathwayAttainmentsDataProvider courseId={courseId}>
          <SkillsDialog
            open={open}
            onClose={onClose}
            focusedSkillAreaId={focusedSkillAreaId}
            item={item}
            itemMetadata={itemMetadata}
            submissionState={submissionState}
            refetchAssignmentSubmissionData={refetchAssignmentSubmissionData}
            trackingData={trackingData}
            renderRetryActionButton={renderRetryActionButton}
            enterpriseContextProgram={enterpriseContextProgram}
          />
        </SkillPathwayAttainmentsDataProvider>
      </VerifiedSkillsBadgeDataProvider>
    </SkillPathwayEnrollmentDataProvider>
  );
};

const SkillsDialogWithEnterpriseProgramData = ({
  open,
  onClose,
  courseId,
  focusedSkillAreaId,
  item,
  itemMetadata,
  submissionState,
  refetchAssignmentSubmissionData,
  trackingData,
  renderRetryActionButton,
}: WrapperProps) => {
  if (!open) {
    return null;
  }

  return (
    <EnterpriseProgramDataProvider>
      <SkillsDialogWithAttainmentData
        open={open}
        onClose={onClose}
        courseId={courseId}
        focusedSkillAreaId={focusedSkillAreaId}
        item={item}
        itemMetadata={itemMetadata}
        submissionState={submissionState}
        refetchAssignmentSubmissionData={refetchAssignmentSubmissionData}
        trackingData={trackingData}
        renderRetryActionButton={renderRetryActionButton}
      />
    </EnterpriseProgramDataProvider>
  );
};

export default SkillsDialogWithEnterpriseProgramData;
